import { appConstants } from '../../../application/configurations/constants';
import { UtilityService } from '../utils';
import { sha256 } from 'js-sha256';
import { previewConst } from '../../../presentation/components/preview/constants';
import { store } from '../../../application/store';

/**
 * class for passing adobe analytics data based on actions
 */
export class AdobeAnalyticsEvents {
  /**
   * Function to append preview button clicked data to adobe analytics
   * @param {any} item clicked item data
   * @param {any} accounts user account data
   * */
  static previewClick = (item: any, userId: any) => {
    let fileNameVal = item.attachment
      ? item.attachment.replace(/^.*[\\\/]/, '')
      : '';
    fileNameVal = fileNameVal.length > 0 ? fileNameVal.split('.')[0] : '';

    let BrowserData: any = store.getState().browseSearch.browseTitles;
    const urlParams = new URLSearchParams(window.location.href);
    let verticalValue = urlParams.get('v');
    let BrowserTitle =
      BrowserData && BrowserData.length > 0
        ? BrowserData[0].title
        : verticalValue;
    const pageLocation = window.location.href
      .split('?')[0]
      .toString()
      .includes('results')
      ? 'Discover Search Result'
      : BrowserTitle.concat(' browse search result');

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.previewData.event,
      web: {
        webInteraction: {
          name: appConstants.adobeAnalytics.previewData.name,
          type: appConstants.adobeAnalytics.previewData.type,
          linkClicks: { value: 1 },
          url: item.url ? item.url : ''
        },
        _etassureco: {
          userID: userId,
          eyProduct: appConstants.adobeAnalytics.previewData.eyProduct,
          eyApp: appConstants.adobeAnalytics.previewData.eyApp,
          controlType: appConstants.adobeAnalytics.previewData.controlType,
          search: {
            resultClicks: { value: 1 }
          },
          content: {
            id: item.ESC_EYAEMID,
            URL: item.url,
            language: appConstants.adobeAnalytics.previewData.language,
            createdDate: item.createdDate
              ? new Date(item.createdDate).toISOString()
              : '',
            title: item.title ? UtilityService.removeHtmlTags(item.title) : '',
            filename: fileNameVal,
            fileFormat: item.fileType,
            author: sha256(item.authorEmail),
            category: item.contentType,
            views: { value: 1 }
          }
        }
      },
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage: pageLocation,
          instances: 1
        }
      }
    });
  };

  /**
   * Function appends data to adobe analytics when "did you know" button clicked
   * @param {any} item clicked item data
   * @param {any} accounts user account data
   * */

  static dykClick = (window: any, userId: any) => {
    const PageURL = window.location.href.split('?');
    let pageName =
      PageURL.length > 1
        ? PageURL[0].toLowerCase().includes('browse')
          ? appConstants.adobeAnalytics.didYouKnowData.browse
          : appConstants.adobeAnalytics.didYouKnowData.search
        : '';
    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.didYouKnowData.event,
      web: {
        webPageDetails: {
          name: pageName,
          URL: window.location.href
        },
        webInteraction: {
          name: appConstants.adobeAnalytics.didYouKnowData.name,
          type: appConstants.adobeAnalytics.didYouKnowData.type,
          linkClicks: { value: 1 }
        },
        _etassureco: {
          userID: userId,
          eyProduct: appConstants.adobeAnalytics.didYouKnowData.eyProduct,
          eyApp: appConstants.adobeAnalytics.didYouKnowData.eyApp,
          controlType: appConstants.adobeAnalytics.didYouKnowData.controlType
        }
      }
    });
  };

  /**
   * Function to append clicked data to adobe analytics
   * @param {string} action event value
   * @param {any} items clicked item data
   * @param {any} accounts user account data
   * @param {any} typeOfView view type of clicked results
   * @param {number} pageNumber current page number
   * */
  static resultsClick = (
    action: string,
    items: any,
    userId: any,
    typeOfView: any,
    pageNumber: number
  ) => {
    let hashedDetailPath: string = items.path;
    let Title: string = items.title;
    if (items.type === appConstants.verticals.People) {
      let decodedPeopleDetailPath = decodeURIComponent(items.path);
      let peopleMailId = decodedPeopleDetailPath
        ? decodedPeopleDetailPath.split('|')[
            decodedPeopleDetailPath.split('|').length - 1
          ]
        : '';
      let hashedPeopleMailId = peopleMailId
        ? sha256(peopleMailId.toLowerCase())
        : '';
      Title = Title
        ? sha256(UtilityService.removeHtmlTags(Title).toLowerCase())
        : '';
      hashedDetailPath = decodedPeopleDetailPath
        .replace('i:0#', 'i%3A0%23')
        .replace(peopleMailId, hashedPeopleMailId);
    } else {
      Title = Title ? UtilityService.removeHtmlTags(Title) : '';
    }
    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.event,
      action:
        action === appConstants.adobeAnalytics.preview
          ? appConstants.adobeAnalytics.preview
          : appConstants.adobeAnalytics.view,
      web: {
        webInteraction: {
          name: Title,
          type: appConstants.adobeAnalytics.type
        },
        _etassureco: {
          userId: userId,
          linkId: hashedDetailPath || items.detailPath,
          search: {
            itemPage: pageNumber
          }
        },
        siteKnowledge: {
          supportSiteSearch: {
            locationPage: typeOfView
          }
        }
      }
    });
  };

  /**
   * Function to send events to adobe analytics on page load and no page load
   * @param   {string} searchTerm  search term
   * @param   {number} numberOfResults Number of results
   * @param   {any} userId user if
   * @param   {string} vertical vertical value
   */
  static injectAdobePageLoadEvents = (
    searchTerm: string,
    numberOfResults: number,
    userInfo: any,
    vertical: string,
    noPageload: boolean
  ) => {
    const browseTitleMapping: { [key: string]: string } = {
      proposals: 'Proposals',
      policyfinder: 'Policy Finder',
      solutions: 'Solutions',
      credentials: 'Credentials',
      eypkc: 'EY-Parthenon Knowledge Capture',
      'rm-topics': 'Risk Management Topics'
    };

    const urlParams = new URLSearchParams(window.location.href);
    let verticalValue = urlParams.get('v')?.toLocaleLowerCase() || '';
    let browseTitle = browseTitleMapping[verticalValue]
      ? browseTitleMapping[verticalValue]
      : verticalValue;
    const pageLocation = window.location.href
      .split('?')[0]
      .toString()
      .includes('results')
      ? appConstants.adobeAnalytics.pageLoadEvents.locationInPage
          .searchResultsPage
      : browseTitle.concat(
          appConstants.adobeAnalytics.pageLoadEvents.locationInPage
            .browseResultsPage
        );

    const searchQueryType =
      searchTerm && searchTerm.includes('ESC_ENTERPRISEKEYWORDS')
        ? searchTerm.split(':')[0]
        : null;

    if (!noPageload) {
      const dataLayerPush: any = {
        // Define dataLayerPush
        siteKnowledge: {
          supportSiteSearch: {
            term: searchTerm,
            numberOfResults: numberOfResults,
            instances: 1,
            locationInPage: pageLocation
          }
        },
        web: {
          _etassureco: {
            userId: userInfo.userEmail ? userInfo.userEmail : '',
            search: {
              vertical: vertical
            }
          }
        }
      };

      // Set nullInstances to 1 if numberOfResults is 0, otherwise set to 0
      dataLayerPush.siteKnowledge.supportSiteSearch.nullInstances =
        numberOfResults === 0 ? 1 : 0;

      // Add SearchQueryType if it is not null
      if (searchQueryType) {
        dataLayerPush.web._etassureco.SearchQueryType = searchQueryType;
      }

      window.adobeDataLayer.push(dataLayerPush);

      window.adobeDataLayer.push({
        event: appConstants.adobeAnalytics.pageLoadEvents.pageLoadEventName,
        web: {
          webPageDetails: {
            name: document.title,
            isHomePage: false,
            isErrorPage: false,
            pageUrl: window.location.href
          },
          _etassureco: {
            userId: userInfo.userEmail ? userInfo.userEmail : '',
            eyProduct: appConstants.adobeAnalytics.pageLoadEvents.eyProduct,
            eyApp: appConstants.adobeAnalytics.pageLoadEvents.eyApp,
            userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : ''
          }
        }
      });
    }
  };

  /**
   * Function to send events to adobe analytics on document download
   * @param   {string} uniqueId unique id of document
   * @param   {string} url url of the downloaded document
   */
  static downloadContent = (uniqueId: string, url: string) => {
    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.Download.event,
      web: {
        webInteraction: {
          linkClicks: { value: 1 },
          name: appConstants.adobeAnalytics.Download.name + uniqueId,
          type: appConstants.adobeAnalytics.Download.type,
          URL: url
        },

        _etassureco: {
          content: {
            id: uniqueId,
            downloads: { value: 1 }
          }
        }
      }
    });
  };
  /**
   * Function to send events to adobe analytics on  download/share icon click in preview modal popup
   * @param   {string} item clicked item data
   * @param   {string} userInfo user  Information
   * @param   {string} label label of the clicked icon
   */

  static previewIconTrack = (item: any, userInfo: any, label: any) => {
    let fileNameVal = item.attachment
      ? item.attachment.replace(/^.*[\\\/]/, '')
      : '';
    let BrowserData: any = store.getState().browseSearch.browseTitles;
    const urlParams = new URLSearchParams(window.location.href);
    let verticalValue = urlParams.get('v');
    let BrowserTitle =
      BrowserData && BrowserData.length > 0
        ? BrowserData[0].title
        : verticalValue;
    let pageName = window.location.href
      .split('?')[0]
      .toLowerCase()
      .includes('browse')
      ? BrowserTitle +
        ' ' +
        appConstants.adobeAnalytics.previewData.locationBrowse
      : appConstants.adobeAnalytics.previewData.locationSearch;
    let authorEmail = item.authorEmail ? item.authorEmail.toLowerCase() : '';
    let formattedAuthorEmail = '';
    if (authorEmail.includes(';')) {
      let emails = authorEmail.split(';');
      for (let email of emails) {
        formattedAuthorEmail += sha256(email.trim()) + ',';
      }
      formattedAuthorEmail = formattedAuthorEmail.slice(0, -1);
    } else {
      formattedAuthorEmail = sha256(authorEmail);
    }
    let contentData: any = {
      id: item.ESC_EYAEMID ? item.ESC_EYAEMID : '',
      title: item.title ? UtilityService.removeHtmlTags(item.title) : '',
      url: item.attachment ? item.attachment : '',
      createdDate: item.createdDate
        ? new Date(
            new Date(item.createdDate).getTime() -
              new Date().getTimezoneOffset() * 60000
          ).toISOString()
        : '',

      fileName: fileNameVal,
      fileFormat: item.fileType ? item.fileType : '',
      language: appConstants.adobeAnalytics.previewData.languagePreviewIcon,
      author: formattedAuthorEmail
    };

    if (label === previewConst.downloadLabel) {
      contentData.downloads = { value: 1 };
    }

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.previewData.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInpage: pageName
        }
      },
      web: {
        webPageDetails: {
          URL: item.url ? item.url : '',
          name: item.title ? UtilityService.removeHtmlTags(item.title) : '',
          isHomePage: appConstants.adobeAnalytics.previewData.isPage,
          isErrorPage: appConstants.adobeAnalytics.previewData.isPage
        },
        webInteraction: {
          linkClicks: { value: 1 },
          name: appConstants.adobeAnalytics.previewData.name + label,
          type: appConstants.adobeAnalytics.previewData.type,
          url: item.url ? item.url : ''
        },
        _etassureco: {
          userId: userInfo.userEmail ? userInfo.userEmail : '',
          controlType: appConstants.adobeAnalytics.previewData.iconControl,
          eyProduct: appConstants.adobeAnalytics.previewData.eyProduct,
          eyApp: appConstants.adobeAnalytics.previewData.eyApp,
          userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : '',
          content: contentData
        }
      }
    });
  };

  /**
   * Function to append clicked data to adobe analytics
   * @param {string} enterpriseKeyword search term
   * @param {any} userid user id
   * @param {string} bestbetName bestbet title
   * @param {string} bestbetClickedURL current page number
   * @param {any} userId current page number
   * @param {any} aemId current page number
   * @param {any} createdDate current page number
   */
  static bestbetLinkClick = (
    enterpriseKeyword: string,
    userId: any,
    bestbetName: string,
    bestbetClickedURL: string,
    aemId: any,
    createdDate: any
  ) => {
    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.Bestbets.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage: appConstants.adobeAnalytics.Bestbets.locationInPage,
          term: enterpriseKeyword
        }
      },
      web: {
        webInteraction: {
          name: bestbetName,
          type: appConstants.adobeAnalytics.Bestbets.type,
          URL: bestbetClickedURL
        },
        _etassureco: {
          userId: userId,
          eyProduct: appConstants.adobeAnalytics.Bestbets.eyProduct,
          eyApp: appConstants.adobeAnalytics.Bestbets.eyApp,
          controlType: appConstants.adobeAnalytics.Bestbets.controlType,
          content: {
            title: bestbetName,
            id: aemId,
            url: bestbetClickedURL,
            createdDate: createdDate,
            language: appConstants.adobeAnalytics.Bestbets.language
          }
        }
      }
    });
  };
  /**Function to send events to adobe analytics on click of insights link
   * @param searchTerm search term
   * @param linkTitle link title
   * @param linkUrl link url
   * @param controlType control type
   * @param userId user id
   */

  static insightsLinkClick = (
    searchTerm: string,
    linkTitle: string,
    linkUrl: string,
    controlType: string,
    userId: any,
    analyticsContent: any
  ) => {
    // Extract the email address from linkUrl
    let email = linkUrl.split('|').pop();
    // Hash the email address
    let hashedEmail = email ? sha256(email.toLowerCase()) : '';
    // Replace the original email in the linkUrl with the hashed email if defined
    let hashedLinkUrl = email ? linkUrl.replace(email, hashedEmail) : linkUrl;

    // Combine the original title with "People Profile"
    let combinedTitle = `${linkTitle.split(':')[0]}:People Profile`;

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.insightsClick.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage:
            appConstants.adobeAnalytics.insightsClick.locationInPage,
          term: searchTerm
        }
      },
      web: {
        webInteraction: {
          name: combinedTitle, // Use the combined title
          type: appConstants.adobeAnalytics.insightsClick.type,
          URL: hashedLinkUrl // Use the hashed link URL
        },
        _etassureco: {
          userId: userId,
          eyProduct: appConstants.adobeAnalytics.insightsClick.eyProduct,
          eyApp: appConstants.adobeAnalytics.insightsClick.eyApp,
          controlType: controlType,
          content: {
            id: analyticsContent.titleId,
            title: analyticsContent.docTitle,
            url: analyticsContent.docUrl
          }
        }
      }
    });
  };

  /**
   * Function appends data to adobe analytics when "Access This Content" button clicked
   * @param {any} item clicked item data
   * @param {any}  userInfo user  Information
   * */
  static accessThisContentClick = (item: any, userInfo: any) => {
    let fileNameVal = item.attachment
      ? item.attachment.replace(/^.*[\\\/]/, '')
      : '';
    fileNameVal = fileNameVal.length > 0 ? fileNameVal.split('.')[0] : '';
    let BrowserData: any = store.getState().browseSearch.browseTitles;
    const urlParams = new URLSearchParams(window.location.href);
    let verticalValue = urlParams.get('v');
    let BrowserTitle =
      BrowserData && BrowserData.length > 0
        ? BrowserData[0].title
        : verticalValue;
    let pageName = window.location.href
      .split('?')[0]
      .toLowerCase()
      .includes('browse')
      ? BrowserTitle +
        ' ' +
        appConstants.adobeAnalytics.previewData.locationBrowse
      : appConstants.adobeAnalytics.previewData.locationSearch;

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.previewData.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage: pageName
        }
      },
      web: {
        webPageDetails: {
          URL: window.location.href,
          name: pageName,
          isHomePage: false,
          isErrorPage: false
        },
        webInteraction: {
          linkClicks: { value: 1 },
          name: appConstants.adobeAnalytics.previewData.accessThisContent.name,
          type: appConstants.adobeAnalytics.previewData.type,
          url: item.url ? item.url : ''
        },
        _etassureco: {
          userId: userInfo.userEmail ? userInfo.userEmail : '',
          controlType:
            appConstants.adobeAnalytics.previewData.accessThisContent
              .controlType,
          eyProduct: appConstants.adobeAnalytics.previewData.eyProduct,
          eyApp: appConstants.adobeAnalytics.previewData.eyApp,
          userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : '',
          content: {
            id: item.ESC_EYAEMID,
            title: item.title ? UtilityService.removeHtmlTags(item.title) : '',
            url: item.url,
            createdDate: item.createdDate
              ? new Date(item.createdDate).toISOString()
              : '',
            fileName: fileNameVal,
            fileFormat: item.fileType,
            language:
              appConstants.adobeAnalytics.previewData.languagePreviewIcon,
            author: sha256(item.authorEmail)
          }
        }
      }
    });
  };

  /**
   * Function appends data to adobe analytics when "Relevant Search" blue pill clicked
   * @param {any}  userInfo user  Information
   * @param {string} queryParamText query parameter of the destination url
   * @param {string} verticalText vertical selected
   * */
  static relatedSearchClick = (
    userInfo: any,
    queryParamText: string,
    verticalText: string
  ) => {
    const urlParams = new URLSearchParams(queryParamText);
    const searchTerm = decodeURI(urlParams.get('k') || '');

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.relatedSearches.event,
      siteKnowledge: {
        supportSiteSearch: {
          term: searchTerm,
          instances: 1,
          searchType: verticalText
        }
      },
      web: {
        webPageDetails: {
          URL: window.location.href,
          name: document.title,
          isHomePage: false,
          isErrorPage: false,
          pageUrl: window.location.href
        },
        webInteraction: {
          linkClicks: { value: 1 },
          name: appConstants.adobeAnalytics.relatedSearches.webInteraction.name,
          type: appConstants.adobeAnalytics.relatedSearches.webInteraction.type,
          url: window.location.href.split('?')[0] + queryParamText
        },
        _etassureco: {
          userId: userInfo.userEmail ? userInfo.userEmail : '',
          linkId: appConstants.adobeAnalytics.relatedSearches.etassureco.linkId,
          controlType:
            appConstants.adobeAnalytics.relatedSearches.etassureco.controlType,
          eyProduct:
            appConstants.adobeAnalytics.relatedSearches.etassureco.eyProduct,
          eyApp: appConstants.adobeAnalytics.relatedSearches.etassureco.eyApp,
          userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : ''
        }
      }
    });
  };

  /**
   * Function to share link ellipsis clicked
   * @param {any} item clicked item data
   * @param {any}  userInfo user  Information
   * */
  static shareLinkClick = (item: any, userInfo: any) => {
    let fileNameVal = '';
    if (Array.isArray(item.attachment)) {
      fileNameVal = item.attachment[0]
        ? item.attachment[0].replace(/^.*[\\\/]/, '')
        : '';
    } else {
      fileNameVal = item.attachment
        ? item.attachment.replace(/^.*[\\\/]/, '')
        : '';
    }
    const urlParams = new URLSearchParams(window.location.href);
    const verticalValue = urlParams.get('v') || '';
    const browseConfigs: any = appConstants.adobeAnalytics.browseConfigs;

    let decodedPeopleDetailPath = decodeURIComponent(item.detailPath);
    if (item.cardType === 'People') {
      let peopleMailId = decodedPeopleDetailPath
        ? decodedPeopleDetailPath.split('|')[
            decodedPeopleDetailPath.split('|').length - 1
          ]
        : '';
      let hashedPeopleMailId = peopleMailId
        ? sha256(peopleMailId.toLowerCase())
        : '';
      decodedPeopleDetailPath = decodedPeopleDetailPath
        .replace('i:0#', 'i%3A0%23')
        .replace(peopleMailId, hashedPeopleMailId);
    }

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.documentDownloadAndShareLink.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage: window.location.href
            .split('?')[0]
            .toString()
            .includes('results')
            ? appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPage
            : browseConfigs[verticalValue] +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPageBrowse
        }
      },
      web: {
        webPageDetails: {
          URL: window.location.href,
          name: document.title,
          isHomePage: false,
          isErrorPage: false
        },
        webInteraction: {
          linkClicks: {
            value: 1
          },
          name: window.location.href
            .split('?')[0]
            .toString()
            .includes('results')
            ? appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPage +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .ellipsisShare
            : browseConfigs[verticalValue] +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPageBrowse +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .ellipsisShare,
          type: appConstants.adobeAnalytics.documentDownloadAndShareLink.type,
          url:
            item.cardType === 'People'
              ? decodedPeopleDetailPath ?? ''
              : item.url ?? ''
        },
        _etassureco: {
          userId: userInfo.userEmail ? userInfo.userEmail : '',
          controlType:
            appConstants.adobeAnalytics.documentDownloadAndShareLink
              .controlType,
          eyProduct:
            appConstants.adobeAnalytics.documentDownloadAndShareLink.eyProduct,
          eyApp: appConstants.adobeAnalytics.documentDownloadAndShareLink.eyApp,
          userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : '',
          content: {
            id: item.ESC_EYAEMID ? item.ESC_EYAEMID : '',
            title: item.title ? UtilityService.removeHtmlTags(item.title) : '',
            url: item.url ? item.url : '',
            createdDate: item.createdDate
              ? new Date(item.createdDate).toISOString()
              : '',
            fileName: fileNameVal,
            fileFormat: item.fileType ? item.fileType : '',
            language:
              appConstants.adobeAnalytics.documentDownloadAndShareLink.language,
            author: sha256(item.authorEmail ? item.authorEmail : '')
          }
        }
      }
    });
  };

  /**
   * Function to download document ellipsis clicked
   * @param {any} item clicked item data
   * @param {any}  userInfo user  Information
   * */
  static documentDownloadClick = (item: any, userInfo: any) => {
    let fileNameVal = '';
    if (Array.isArray(item.attachment)) {
      fileNameVal = item.attachment[0]
        ? item.attachment[0].replace(/^.*[\\\/]/, '')
        : '';
    } else {
      fileNameVal = item.attachment
        ? item.attachment.replace(/^.*[\\\/]/, '')
        : '';
    }

    const urlParams = new URLSearchParams(window.location.href);
    const verticalValue = urlParams.get('v') || '';
    const browseConfigs: any = appConstants.adobeAnalytics.browseConfigs;

    window.adobeDataLayer.push({
      event: appConstants.adobeAnalytics.documentDownloadAndShareLink.event,
      siteKnowledge: {
        supportSiteSearch: {
          locationInPage: window.location.href
            .split('?')[0]
            .toString()
            .includes('results')
            ? appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPage
            : browseConfigs[verticalValue] +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPageBrowse
        }
      },
      web: {
        webPageDetails: {
          URL: window.location.href,
          name: document.title,
          isHomePage: false,
          isErrorPage: false
        },
        webInteraction: {
          linkClicks: {
            value: 1
          },
          name: window.location.href
            .split('?')[0]
            .toString()
            .includes('results')
            ? appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPage +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .ellipsisDownload
            : browseConfigs[verticalValue] +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .locationInPageBrowse +
              appConstants.adobeAnalytics.documentDownloadAndShareLink
                .ellipsisDownload,
          type: appConstants.adobeAnalytics.documentDownloadAndShareLink.type,
          url: item.url ? item.url : ''
        },
        _etassureco: {
          userId: userInfo.userEmail ? userInfo.userEmail : '',
          controlType:
            appConstants.adobeAnalytics.documentDownloadAndShareLink
              .controlType,
          eyProduct:
            appConstants.adobeAnalytics.documentDownloadAndShareLink.eyProduct,
          eyApp: appConstants.adobeAnalytics.documentDownloadAndShareLink.eyApp,
          userGeoLocation: userInfo.EYCountry ? userInfo.EYCountry : '',
          content: {
            id: item.ESC_EYAEMID ? item.ESC_EYAEMID : '',
            title: item.title ? UtilityService.removeHtmlTags(item.title) : '',
            url: item.url ? item.url : '',
            createdDate: item.createdDate
              ? new Date(item.createdDate).toISOString()
              : '',
            fileName: fileNameVal,
            fileFormat: item.fileType ? item.fileType : '',
            language:
              appConstants.adobeAnalytics.documentDownloadAndShareLink.language,
            author: sha256(item.authorEmail ? item.authorEmail : ''),
            downloads: {
              value: 1
            }
          }
        }
      }
    });
  };
}
